import { useEffect, useState } from "react";
import { ClipboardText, X, CircleNotch, Fire, Gift, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import DictateButton from "./DictateButton";
import { WheelModal } from "./WheelModal";
import { RewardsShopModal } from "./RewardsShopModal";

export default function ShiftWrapCard() {
  const [due, setDue] = useState(null);
  const [open, setOpen] = useState(null);
  const [prompts, setPrompts] = useState(null);
  const [answers, setAnswers] = useState({});
  const [safety, setSafety] = useState({ complaints: { yes: false, detail: "" }, incident: { yes: false, detail: "" }, near_miss: { yes: false, detail: "" }, concern: { yes: false, detail: "" } });
  const [freeNote, setFreeNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [rewards, setRewards] = useState(null);
  const [wheelOpen, setWheelOpen] = useState(false);
  const [points, setPoints] = useState(null);
  const [shopOpen, setShopOpen] = useState(false);
  const [cpGrants, setCpGrants] = useState([]);
  const [cpAck, setCpAck] = useState(false);

  const load = () => api.get("/shift-notes/due").then((r) => setDue(r.data.due || [])).catch(() => setDue([]));
  const loadRewards = () => api.get("/shift-notes/rewards/me").then((r) => setRewards(r.data)).catch(() => setRewards(null));
  const loadPoints = () => api.get("/points/me").then((r) => setPoints(r.data)).catch(() => setPoints(null));
  useEffect(() => { load(); loadRewards(); loadPoints(); api.get("/my/careplans").then((r) => setCpGrants(r.data.clients || [])).catch(() => {}); }, []);

  const openForm = async (item) => {
    setOpen(item);
    setCpAck(false);
    setAnswers({});
    setSafety({ complaints: { yes: false, detail: "" }, incident: { yes: false, detail: "" }, near_miss: { yes: false, detail: "" }, concern: { yes: false, detail: "" } });
    setFreeNote("");
    setPrompts(null);
    try {
      const { data } = await api.get("/shift-notes/prompts", { params: { client_id: item.client_id, date: item.shift_date } });
      setPrompts(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setOpen(null);
    }
  };

  const submit = async () => {
    const ans = [
      ...(prompts?.rotating || []).map((q) => ({ q, a: answers[`r:${q}`] || "", kind: "rotating" })),
      ...(prompts?.client_questions || []).map((q) => ({ q, a: answers[`c:${q}`] || "", kind: "client" })),
    ].filter((a) => a.a.trim());
    const monitoring = (prompts?.monitoring || [])
      .map((q) => ({ id: q.id, answer: String(answers[`m:${q.id}`] || "").trim() }))
      .filter((m) => m.answer);
    if (!ans.length && !freeNote.trim() && !monitoring.length) { toast.error("Answer at least one question or write a note"); return; }
    setBusy(true);
    try {
      if (cpAck && cpGrants.some((g) => g.client_id === open.client_id)) {
        await api.post(`/clients/${open.client_id}/careplan-ack`, { shift_date: open.shift_date }).catch(() => {});
      }
      // task codes are confirmed in HHAeXchange EVV — the wrap-up documents PROCEDURES only
      // (owner 2026-09-06: no attestation here either; accountability = plan checked against
      // the uploaded duty codes, never the caregiver's word)
      const { data } = await api.post("/shift-notes", {
        client_id: open.client_id, visit_ref: open.visit_ref, shift_date: open.shift_date,
        answers: ans, safety, free_note: freeNote, monitoring,
      });
      const fl = data.flags || {};
      const gainedSpin = (data.rewards?.spins_available || 0) > (rewards?.spins_available || 0);
      const ptsNote = data.points_earned ? ` (+${data.points_earned} pts)` : "";
      if (fl.incident || fl.complaint) {
        toast.success(`Wrap-up saved${ptsNote} — the office has been flagged to review it`, { duration: 6000 });
      } else if (gainedSpin) {
        toast.success(`Wrap-up saved${ptsNote} — you earned a wheel spin!`, { duration: 6000 });
      } else {
        toast.success(`Shift wrap-up saved${ptsNote} — thank you`);
      }
      setOpen(null);
      load();
      loadRewards();
      loadPoints();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!due) return null;

  return (
    <div className="mb-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="shift-wrap-card">
      <div className="flex items-center gap-2">
        <ClipboardText size={18} className="text-primary" />
        <p className="font-heading text-sm font-semibold text-stone-900">Shift wrap-up notes</p>
        <span className="ml-auto flex items-center gap-2">
          {points && points.enabled && (
            <button onClick={() => setShopOpen(true)}
              className="inline-flex items-center gap-1 rounded-full border border-[#4A235A]/40 bg-[#4A235A]/10 px-2.5 py-0.5 text-xs font-semibold text-[#4A235A] transition-transform hover:scale-105"
              data-testid="wrap-points-chip" title="Your points — tap for the swag shop">
              <Star size={13} weight="fill" /> {points.balance} pts
            </button>
          )}
          {rewards && rewards.enabled && rewards.current_streak > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full border border-[#B7791F]/30 bg-[#B7791F]/10 px-2.5 py-0.5 text-xs font-semibold text-[#B7791F]"
              data-testid="wrap-streak-chip" title={`Best streak: ${rewards.best_streak}`}>
              <Fire size={13} weight="fill" /> {rewards.current_streak}-shift streak
            </span>
          )}
          {rewards && rewards.enabled && (rewards.spins_available > 0 ? (
            <button onClick={() => setWheelOpen(true)}
              className="inline-flex items-center gap-1 rounded-full border border-[#4A235A]/30 bg-[#4A235A]/10 px-2.5 py-0.5 text-xs font-semibold text-[#4A235A] transition-transform hover:scale-105"
              data-testid="wrap-spin-cta">
              <Gift size={13} weight="fill" /> {rewards.spins_available} spin{rewards.spins_available === 1 ? "" : "s"} waiting!
            </button>
          ) : rewards.current_streak > 0 ? (
            <span className="text-[10px] text-stone-400">every on-time wrap = a spin · perfect week = bonus spin</span>
          ) : <span className="text-[10px] text-stone-400">wrap up a shift on time to earn a spin</span>)}
        </span>
      </div>
      {due.length === 0 ? (
        <p className="mt-2 text-sm text-stone-500" data-testid="shift-wrap-empty">All caught up — every recent shift has a wrap-up note. Thank you.</p>
      ) : (
        <>
          <p className="mt-1 text-xs text-stone-500">A quick note after each shift — 2 minutes, you can dictate. It keeps the office and the next caregiver in the loop.</p>
          <div className="mt-3 space-y-2">
            {due.map((d) => (
              <div key={`${d.client_id}-${d.shift_date}`} className="flex items-center justify-between rounded-lg border border-stone-200 px-4 py-2.5" data-testid={`shift-wrap-due-${d.client_id}-${d.shift_date}`}>
                <div>
                  <p className="text-sm font-medium text-stone-900">{d.client_name}</p>
                  <p className="text-xs text-stone-400">{d.shift_date}</p>
                </div>
                <Button size="sm" onClick={() => openForm(d)} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`shift-wrap-open-${d.client_id}-${d.shift_date}`}>Write wrap-up</Button>
              </div>
            ))}
          </div>
        </>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" data-testid="shift-wrap-modal">
          <div className="my-8 w-full max-w-xl rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-heading text-lg font-semibold text-stone-900">Shift wrap-up — {open.client_name}</p>
                <p className="text-xs text-stone-500">{open.shift_date}</p>
              </div>
              <button onClick={() => setOpen(null)} className="text-stone-400 hover:text-stone-700" data-testid="shift-wrap-close"><X size={20} /></button>
            </div>
            {!prompts ? (
              <div className="flex justify-center py-10"><CircleNotch size={24} className="animate-spin text-primary" /></div>
            ) : (
              <div className="mt-4 space-y-4">
                {prompts.rotating.map((q) => (
                  <div key={q}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-stone-800">{q}</p>
                      <DictateButton testid={`wrap-dictate-${q.slice(0, 20)}`} value={answers[`r:${q}`] || ""}
                        onText={(t) => setAnswers((a) => ({ ...a, [`r:${q}`]: ((a[`r:${q}`] || "") + " " + t).trim() }))}
                        onReplace={(t) => setAnswers((a) => ({ ...a, [`r:${q}`]: t }))} />
                    </div>
                    <Textarea rows={2} value={answers[`r:${q}`] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [`r:${q}`]: e.target.value }))} className="mt-1.5 border-stone-300" data-testid={`wrap-answer-${q.slice(0, 20)}`} />
                  </div>
                ))}
                {prompts.client_questions.map((q) => (
                  <div key={q}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-stone-800">{q} <span className="text-xs text-primary">(asked every shift for this client)</span></p>
                      <DictateButton testid={`wrap-dictate-c-${q.slice(0, 20)}`} value={answers[`c:${q}`] || ""}
                        onText={(t) => setAnswers((a) => ({ ...a, [`c:${q}`]: ((a[`c:${q}`] || "") + " " + t).trim() }))}
                        onReplace={(t) => setAnswers((a) => ({ ...a, [`c:${q}`]: t }))} />
                    </div>
                    <Textarea rows={2} value={answers[`c:${q}`] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [`c:${q}`]: e.target.value }))} className="mt-1.5 border-stone-300" data-testid={`wrap-answer-c-${q.slice(0, 20)}`} />
                  </div>
                ))}
                {(prompts.monitoring || []).length > 0 && (
                  <div className="rounded-lg border border-[#2F6B4F]/25 bg-[#2F6B4F]/[0.04] p-4" data-testid="wrap-monitoring-block">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#2F6B4F]">Today's check — {open.client_name}</p>
                    <p className="mt-0.5 text-[11px] text-stone-500">Quick taps — the little things the office is watching.</p>
                    <div className="mt-3 space-y-3">
                      {prompts.monitoring.map((q) => (
                        <div key={q.id} data-testid={`wrap-monitoring-${q.id}`}>
                          <p className="text-sm font-medium text-stone-800">{q.prompt}</p>
                          {q.kind === "yesno" && (
                            <div className="mt-1.5 flex gap-2">
                              {["Yes", "No"].map((opt) => (
                                <button key={opt} type="button"
                                  onClick={() => setAnswers((a) => ({ ...a, [`m:${q.id}`]: a[`m:${q.id}`] === opt ? "" : opt }))}
                                  className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${answers[`m:${q.id}`] === opt ? "border-[#2F6B4F] bg-[#2F6B4F] text-white" : "border-stone-300 text-stone-500 hover:border-stone-400"}`}
                                  data-testid={`wrap-monitoring-${q.id}-${opt.toLowerCase()}`}>
                                  {opt}
                                </button>
                              ))}
                            </div>
                          )}
                          {q.kind === "choice" && (
                            <div className="mt-1.5 flex flex-wrap gap-2">
                              {(q.options || []).map((opt) => (
                                <button key={opt} type="button"
                                  onClick={() => setAnswers((a) => ({ ...a, [`m:${q.id}`]: a[`m:${q.id}`] === opt ? "" : opt }))}
                                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${answers[`m:${q.id}`] === opt ? "border-[#2F6B4F] bg-[#2F6B4F] text-white" : "border-stone-300 text-stone-500 hover:border-stone-400"}`}
                                  data-testid={`wrap-monitoring-${q.id}-${opt.slice(0, 12).toLowerCase().replace(/\W+/g, "-")}`}>
                                  {opt}
                                </button>
                              ))}
                            </div>
                          )}
                          {q.kind === "text" && (
                            <Input value={answers[`m:${q.id}`] || ""}
                              onChange={(e) => setAnswers((a) => ({ ...a, [`m:${q.id}`]: e.target.value }))}
                              className="mt-1.5 h-8 border-stone-300 text-sm" placeholder="type here…"
                              data-testid={`wrap-monitoring-${q.id}-input`} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="rounded-lg border border-stone-200 bg-stone-50 p-4" data-testid="wrap-safety-block">
                  <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Every shift — please answer all four</p>
                  {prompts.safety.map((s) => (
                    <div key={s.key} className="mt-3">
                      <p className="text-sm font-medium text-stone-800">{s.q}</p>
                      <div className="mt-1.5 flex gap-2">
                        {[["yes", "Yes"], ["no", "No"]].map(([val, label]) => (
                          <button key={val} type="button"
                            onClick={() => setSafety((cur) => ({ ...cur, [s.key]: { ...cur[s.key], yes: val === "yes" } }))}
                            className={`rounded-full border px-4 py-1 text-sm font-medium transition-colors ${(val === "yes") === safety[s.key].yes ? "border-primary bg-primary text-primary-foreground" : "border-stone-300 text-stone-600 hover:border-primary"}`}
                            data-testid={`wrap-safety-${s.key}-${val}`}>{label}</button>
                        ))}
                      </div>
                      {safety[s.key].yes && (
                        <Textarea rows={2} placeholder="What happened? Who, what, when…" value={safety[s.key].detail}
                          onChange={(e) => setSafety((cur) => ({ ...cur, [s.key]: { ...cur[s.key], detail: e.target.value } }))}
                          className="mt-2 border-stone-300 bg-white" data-testid={`wrap-safety-${s.key}-detail`} />
                      )}
                    </div>
                  ))}
                  {Object.values(safety).some((s) => s.yes) && (
                    <div className="mt-3 rounded-md border border-[#C0392B]/40 bg-[#C0392B]/10 p-2.5" data-testid="wrap-call-now-banner">
                      <p className="text-xs font-bold text-[#C0392B]">Call the office right now — 800-719-6912 (24-hour line). Don't rely on this note alone.</p>
                      <p className="mt-1 text-[11px] text-[#C0392B]/80">Report it in real time, then finish documenting here. The office will point you to the right form (complaint or incident) to fill out.</p>
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-stone-800">Anything else the office should know? (optional)</p>
                    <DictateButton testid="wrap-dictate-free" value={freeNote}
                      onText={(t) => setFreeNote((v) => (v + " " + t).trim())} onReplace={setFreeNote} />
                  </div>
                  <Textarea rows={2} value={freeNote} onChange={(e) => setFreeNote(e.target.value)} className="mt-1.5 border-stone-300" data-testid="wrap-free-note" />
                </div>
                {cpGrants.some((g) => g.client_id === open.client_id) && (
                  <label className="flex items-start gap-2.5 rounded-lg border border-primary/25 bg-primary/[0.04] px-3 py-2.5" data-testid="wrap-careplan-ack-row">
                    <input type="checkbox" className="mt-0.5" checked={cpAck} onChange={(e) => setCpAck(e.target.checked)} data-testid="wrap-careplan-ack" />
                    <span className="text-xs text-stone-600">
                      <b>Care plan sign-off.</b> I reviewed this client's care plan today and followed it this shift.
                    </span>
                  </label>
                )}
                <Button onClick={submit} disabled={busy} className="w-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="shift-wrap-submit">
                  {busy ? "Saving…" : "Save wrap-up note"}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      {wheelOpen && rewards && (
        <WheelModal prizes={rewards.prizes || []}
          onDone={() => { setWheelOpen(false); loadRewards(); }} />
      )}
      {shopOpen && points && (
        <RewardsShopModal data={points} onClose={() => setShopOpen(false)}
          onChanged={() => loadPoints()} />
      )}
    </div>
  );
}
